import { CreatePaymentRequest, CreatePaymentRequestResp } from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const creditKeys = {
  all: ["credit"] as const,
  my: () => [...creditKeys.all, "my"] as const,
  transactions: () => [...creditKeys.all, "transactions"] as const,
  userCredits: (userId: string) => [...creditKeys.all, "user", userId] as const,
  userTransactions: (userId: string) => [...creditKeys.all, "user", userId, "transactions"] as const
};

export function useMyCreditQuery() {
  const { creditApiClient } = useApiClients();

  return useQuery({
    queryKey: creditKeys.my(),
    queryFn: () => creditApiClient.getMy(),
    staleTime: 5 * 60 * 1000
  });
}

export function useMyTransactionsQuery() {
  const { creditApiClient } = useApiClients();

  return useQuery({
    queryKey: creditKeys.transactions(),
    queryFn: () => creditApiClient.getMyTransactions(),
    staleTime: 5 * 60 * 1000
  });
}

export function useCreditInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: creditKeys.my() });
    queryClient.invalidateQueries({ queryKey: creditKeys.transactions() });
  };
}

export function useCreatePaymentRequestMutation(
  options: {
    onSuccess?: (result: CreatePaymentRequestResp) => void;
    onError?: (error: unknown) => void;
  } = {}
) {
  const { creditApiClient } = useApiClients();
  const invalidate = useCreditInvalidation();
  return useMutation({
    mutationFn: (data: CreatePaymentRequest) => creditApiClient.createPaymentRequest(data),
    onSuccess: (result) => {
      invalidate();
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useUserCredits(userId: string, options: { enabled?: boolean } = {}) {
  const { creditApiClient } = useApiClients();

  return useQuery({
    queryKey: creditKeys.userCredits(userId),
    queryFn: () => creditApiClient.getUserCredits(userId),
    enabled: (options.enabled ?? true) && !!userId,
    staleTime: 5 * 60 * 1000
  });
}

export function useUserCreditTransactions(userId: string, options: { enabled?: boolean } = {}) {
  const { creditApiClient } = useApiClients();

  return useQuery({
    queryKey: creditKeys.userTransactions(userId),
    queryFn: () => creditApiClient.getUserTransactions(userId),
    enabled: (options.enabled ?? true) && !!userId,
    staleTime: 5 * 60 * 1000
  });
}
